import { Graphics } from 'pixi.js';
import { WARP_CONFIG } from '@astra/shared';

// WarpVisual — self-contained warp animation, copied from the player's warp effect
// (RemotePlayer.updateWarpCalculationEffect / updateWarpTrailEffect) so drones warp with
// the same look. Owns its own Graphics (added to the world container by the scene) and its
// own particle state; clear-then-draw each frame so it's actually visible (the player's
// inline calc effect is cleared on the same frame and never shows — fixed here).
//
// Usage: create one per warping entity, add `.graphics` to the world container, call
// `update(x, y, vx, vy, phase)` each frame, and `destroy()` when done.
export class WarpVisual {
  public readonly graphics: Graphics;

  // Calculating-phase absorption particles (swirl inward toward the ship).
  private calcParticles: Array<{
    angle: number; dist: number; startDist: number;
    speed: number; size: number; color: number; trail: number;
  }> = [];
  private calcTime = 0;

  // Traveling-phase parallel speed-lines.
  private warpLines: Array<{ pos: number; lateral: number; speed: number; len: number; width: number }> = [];
  private warpTime = 0;

  constructor() {
    this.graphics = new Graphics();
    this.graphics.zIndex = 4;
  }

  /** Render the warp effect at (x, y); `vx,vy` give the travel direction during 'traveling'. */
  update(x: number, y: number, vx: number, vy: number, phase: string): void {
    if (phase === 'calculating') {
      this.renderCalc(x, y);
    } else if (phase === 'traveling') {
      this.renderTrail(x, y, vx, vy);
    } else {
      this.graphics.clear();
      if (this.calcParticles.length) { this.calcParticles = []; this.calcTime = 0; }
      if (this.warpLines.length) { this.warpLines = []; this.warpTime = 0; }
    }
  }

  destroy(): void {
    this.graphics.destroy();
  }

  // ---- copied from RemotePlayer.updateWarpCalculationEffect (x,y parameterized) ----
  private renderCalc(x: number, y: number): void {
    const g = this.graphics;
    g.clear();
    this.calcTime += 0.04;
    const t = this.calcTime;

    const calcPalette = [0x00aaff, 0x00ccff, 0x20a5a6, 0x8855ff, 0xaa44ff, 0x00ddff, 0x4488ff, 0xffffff];
    const pickColor = () => calcPalette[Math.floor(Math.random() * calcPalette.length)];

    const maxRadius = 140;
    const maxParticles = 24; // a touch lighter than the remote player (30)

    while (this.calcParticles.length < maxParticles) {
      const angle = Math.random() * Math.PI * 2;
      const startDist = maxRadius * (0.6 + Math.random() * 0.4);
      this.calcParticles.push({
        angle, dist: startDist, startDist,
        speed: 0.8 + Math.random() * 1.5,
        size: 0.8 + Math.random() * 2.0,
        color: pickColor(),
        trail: 0.3 + Math.random() * 0.7,
      });
    }

    for (let i = this.calcParticles.length - 1; i >= 0; i--) {
      const p = this.calcParticles[i];
      p.dist -= p.speed;
      p.angle += 0.008 + (1 - p.dist / p.startDist) * 0.02;
      if (p.dist < maxRadius * 0.4) p.speed *= 1.02;

      if (p.dist <= 4) {
        const angle = Math.random() * Math.PI * 2;
        const startDist = maxRadius * (0.6 + Math.random() * 0.4);
        p.angle = angle; p.dist = startDist; p.startDist = startDist;
        p.speed = 0.8 + Math.random() * 1.5;
        p.size = 0.8 + Math.random() * 2.0;
        p.color = pickColor();
        p.trail = 0.3 + Math.random() * 0.7;
        continue;
      }

      const px = x + Math.cos(p.angle) * p.dist;
      const py = y + Math.sin(p.angle) * p.dist;
      const normalizedDist = p.dist / maxRadius;
      const alpha = Math.min(0.9, 0.15 + (1 - normalizedDist) * 0.75);

      const trailLen = p.trail * 12 * (1 - normalizedDist + 0.3);
      const trailX = px + Math.cos(p.angle) * trailLen;
      const trailY = py + Math.sin(p.angle) * trailLen;
      g.moveTo(trailX, trailY);
      g.lineTo(px, py);
      g.stroke({ width: p.size * 0.6, color: p.color, alpha: alpha * 0.4 });

      const pSize = p.size + Math.sin(t * 8 + p.angle * 3) * 0.4;
      g.circle(px, py, pSize);
      g.fill({ color: p.color, alpha });

      if (p.size > 1.5) {
        g.circle(px, py, pSize * 0.5);
        g.fill({ color: 0xffffff, alpha: alpha * 0.8 });
      }
    }

    // Central glow
    const glowR = 6 + Math.sin(t * 6) * 2;
    g.circle(x, y, glowR + 10);
    g.fill({ color: 0x00aaff, alpha: 0.03 + Math.sin(t * 4) * 0.015 });
    g.circle(x, y, glowR + 5);
    g.fill({ color: 0x20a5a6, alpha: 0.06 + Math.sin(t * 5) * 0.02 });
    g.circle(x, y, glowR + 2);
    g.fill({ color: 0x00ccff, alpha: 0.15 + Math.sin(t * 7) * 0.05 });
    g.circle(x, y, glowR);
    g.fill({ color: 0xffffff, alpha: 0.4 + Math.sin(t * 8) * 0.1 });
  }

  // ---- copied from RemotePlayer.updateWarpTrailEffect (x,y,vx,vy parameterized) ----
  private renderTrail(x: number, y: number, vx: number, vy: number): void {
    const g = this.graphics;
    g.clear();
    this.warpTime += 0.06;

    const speed = Math.sqrt(vx * vx + vy * vy);
    if (speed < 100) return;

    const velAngle = Math.atan2(vy, vx);
    const cosV = Math.cos(velAngle);
    const sinV = Math.sin(velAngle);
    const cosP = Math.cos(velAngle + Math.PI / 2);
    const sinP = Math.sin(velAngle + Math.PI / 2);
    const warpColor = WARP_CONFIG.WARP_COLOR;
    const t = this.warpTime;

    const ahead = Math.min(speed * 0.3, 250);
    const behind = Math.min(speed * 0.8, 600);
    const totalRange = ahead + behind;
    const spreadRadius = 60;

    // Core trail behind ship (3 layers)
    const coreSegs = 8;
    for (let layer = 0; layer < 3; layer++) {
      const frac = layer / 2;
      const w = 30 * (1 - frac * 0.9);
      const a = 0.03 + frac * 0.1;
      const col = frac > 0.8 ? 0xffffff : warpColor;
      for (let s = 0; s < coreSegs; s++) {
        const r0 = s / coreSegs;
        const r1 = (s + 1) / coreSegs;
        g.moveTo(x - cosV * behind * r0, y - sinV * behind * r0);
        g.lineTo(x - cosV * behind * r1, y - sinV * behind * r1);
        g.stroke({ width: w, color: col, alpha: a * (1 - r0 * 0.7) });
      }
    }

    // Parallel speed-lines
    const maxLines = 18;
    while (this.warpLines.length < maxLines) {
      const lat = (Math.random() - 0.5) * 2;
      this.warpLines.push({
        pos: Math.random(),
        lateral: Math.abs(lat) < 0.15 ? (lat < 0 ? -0.2 : 0.2) : lat,
        speed: 1.0 + Math.random() * 1.5,
        len: 35 + Math.random() * 80,
        width: 0.5 + Math.random() * 1.5,
      });
    }

    for (const line of this.warpLines) {
      line.pos += line.speed * 0.018;
      if (line.pos > 1.15) {
        line.pos = -0.1;
        line.lateral = (Math.random() - 0.5) * 2;
        if (Math.abs(line.lateral) < 0.15) line.lateral = line.lateral < 0 ? -0.2 : 0.2;
        line.speed = 1.0 + Math.random() * 1.5;
      }
      if (line.pos < 0 || line.pos > 1) continue;

      const axisOffset = -ahead + line.pos * totalRange;
      const lat = line.lateral * spreadRadius;
      const halfLen = line.len / 2;
      const cx = x - cosV * axisOffset + cosP * lat;
      const cy = y - sinV * axisOffset + sinP * lat;

      const shipPos = ahead / totalRange;
      const dist = Math.abs(line.pos - shipPos);
      const lineAlpha = Math.max(0.05, 0.45 - dist * 0.8);
      const lineColor = Math.abs(line.lateral) < 0.3 ? 0xffffff : warpColor;

      g.moveTo(cx + cosV * halfLen, cy + sinV * halfLen);
      g.lineTo(cx - cosV * halfLen, cy - sinV * halfLen);
      g.stroke({ width: line.width, color: lineColor, alpha: Math.min(lineAlpha, 0.5) });
    }

    // Core glow at ship
    const coreR = 4 + Math.sin(t * 10) * 1.5;
    g.circle(x, y, coreR + 8);
    g.fill({ color: warpColor, alpha: 0.08 });
    g.circle(x, y, coreR + 3);
    g.fill({ color: warpColor, alpha: 0.2 });
    g.circle(x, y, coreR);
    g.fill({ color: 0xffffff, alpha: 0.65 });
  }
}
